import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { Users, HelpCircle, Settings } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les données par audience
 * Ce composant charge ses propres données depuis l'endpoint Google Ads
 */
const AudiencesTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  token,
  setError,
  rawData = null,
  enableClientSideFiltering = false,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  labelFilteredCampaignIds = null,
  // Totaux globaux du compte pour calculer "sans audiences"
  globalTotals = null,
  // Totaux globaux de la période précédente pour calculer "sans audiences" comparatif
  previousGlobalTotals = null,
  // Couleurs pour les pie charts
  COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7c43'],
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'audiences',
  loadingComparison = false
}) => {
  const selectedMetrics = useSelectedMetrics();

  console.log('👥 [AUDIENCES-COMPONENT] Composant chargé avec:', { selectedAccount: selectedAccount?.name, selectedPeriod, isVisible });

  // États locaux pour les données
  const [audiencesData, setAudiencesData] = useState([]);
  const [previousAudiencesData, setPreviousAudiencesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // États pour le tri
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // État pour le tooltip des types d'audiences
  const [showTypeTooltip, setShowTypeTooltip] = useState(false);

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('audiences', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);





  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(audience.impressions || 0, prevAudience.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.impressions)}
          {hasComparison && renderVariation(calculateVariation(data.impressions, prevData.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.ctr) || 0, prevAudience.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(overallMetrics.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.ctr), previousTotals.ctr))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.ctr, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.ctr), prevData.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(audience.clicks || 0, prevAudience.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.clicks)}
          {hasComparison && renderVariation(calculateVariation(data.clicks, prevData.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(audience.cpc || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.cpc) || 0, prevAudience.cpc || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{"\u20AC"}{formatNumber(overallMetrics.cpc, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.cpc), previousTotals.cpc), true)}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(data.cpc, 2)}
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.cpc), prevData.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(audience.cost || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(audience.cost || 0, prevAudience.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{"\u20AC"}{formatNumber(totals.cost, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(data.cost, 2)}
          {hasComparison && renderVariation(calculateVariation(data.cost, prevData.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.conversionRate || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.conversionRate) || 0, prevAudience.conversionRate || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(overallMetrics.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.conversionRate), previousTotals.conversionRate))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.conversionRate, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.conversionRate), prevData.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(audience.conversions || 0, prevAudience.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.conversions, 1)}
          {hasComparison && renderVariation(calculateVariation(data.conversions, prevData.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (audience) => getCpaClassName(parseFloat(audience.cpa) || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (overallMetrics) => getCpaClassName(parseFloat(overallMetrics.cpa), selectedAccount?.id || 'global', moduleId),
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(audience.cpa || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.cpa), prevAudience.cpa), true)}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{"\u20AC"}{formatNumber(overallMetrics.cpa, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.cpa), previousTotals.cpa), true)}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(data.cpa, 2)}
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.cpa), prevData.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(audience.conversionsValue || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(audience.conversionsValue || 0, prevAudience.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{"\u20AC"}{formatNumber(totals.conversionsValue, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(data.conversionsValue, 2)}
          {hasComparison && renderVariation(calculateVariation(data.conversionsValue, prevData.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (audience) => getCosClassName(parseFloat(audience.cos) || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (overallMetrics) => getCosClassName(parseFloat(overallMetrics.cos), selectedAccount?.id || 'global', moduleId),
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.cos || 0, 1)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.cos), prevAudience.cos), true)}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(overallMetrics.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.cos), previousTotals.cos), true)}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.cos, 1)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.cos), prevData.cos), true)}
        </>
      )
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (audience, prevAudience, hasComparison) => (
        <>
          {formatNumber(audience.roas || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(audience.roas), prevAudience.roas))}
        </>
      ),
      renderTotal: (totals, previousTotals, overallMetrics) => (
        <>
          <strong>{formatNumber(overallMetrics.roas, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(parseFloat(overallMetrics.roas), previousTotals.roas))}
        </>
      ),
      renderSummaryCell: (data, prevData, hasComparison) => (
        <>
          {formatNumber(data.roas, 2)}%
          {hasComparison && renderVariation(calculateVariation(parseFloat(data.roas), prevData.roas))}
        </>
      )
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (audience, prevAudience, hasComparison) => {
        const roi = parseFloat(audience.roi) || 0;
        const prevRoi = prevAudience?.roi || 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals, overallMetrics) => {
        const roi = parseFloat(overallMetrics.roi) || 0;
        const prevRoi = previousTotals?.roi || 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderSummaryCell: (data, prevData, hasComparison) => {
        const roi = parseFloat(data.roi) || 0;
        const prevRoi = prevData?.roi || 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (audience, prevAudience, hasComparison) => {
        const panierMoyen = (audience.conversions || 0) > 0 ? (audience.conversionsValue || 0) / audience.conversions : 0;
        const prevPanierMoyen = prevAudience && (prevAudience.conversions || 0) > 0 ? (prevAudience.conversionsValue || 0) / prevAudience.conversions : 0;
        return (
          <>
            {"\u20AC"}{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals, overallMetrics) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>{"\u20AC"}{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderSummaryCell: (data, prevData, hasComparison) => {
        const panierMoyen = (data.conversions || 0) > 0 ? (data.conversionsValue || 0) / data.conversions : 0;
        const prevPanierMoyen = prevData && (prevData.conversions || 0) > 0 ? (prevData.conversionsValue || 0) / prevData.conversions : 0;
        return (
          <>
            {"\u20AC"}{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);

  // Fonction pour charger les données d'audiences
  const loadAudiencesData = useCallback(async () => {
    if (!selectedAccount || !selectedPeriod || !token) {
      console.log('👥 [AUDIENCES] Conditions non remplies, arrêt du chargement');
      return;
    }

    setLoading(true);

    try {
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod,
        campaignFilter: selectedCampaignFilter || null,
        campaignTypeFilter: selectedCampaignTypeFilter || null
      };

      // Ajouter les campaignIds filtrés par labels si présents
      if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
        requestBody.campaignIds = labelFilteredCampaignIds;
        console.log('👥 [AUDIENCES-API] Filtrage par labels - campaignIds:', labelFilteredCampaignIds.length);
      }

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      console.log('👥 [AUDIENCES] Requête:', requestBody);

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/components/audiences`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('👥 [AUDIENCES] Réponse reçue:', response.data);

      if (response.data.success) {
        const audiences = response.data.data.audiences || [];
        setAudiencesData(audiences);
        setIsDataLoaded(true);
        console.log(`👥 [AUDIENCES] ${audiences.length} audiences chargées`);

        // Charger les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          setLoadingPreviousPeriod(true);
          // Calculer les dates courantes (utiliser customDates ou dériver de selectedPeriod)
          let currentStartDate = customStartDate;
          let currentEndDate = customEndDate;
          if (!currentStartDate || !currentEndDate) {
            const dateRange = getDateRangeFromPeriod(selectedPeriod);
            currentStartDate = dateRange.startDate;
            currentEndDate = dateRange.endDate;
          }
          const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
          console.log(`👥 [AUDIENCES] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

          try {
            const prevRequestBody = {
              sources: [selectedAccount.id],
              period: 'CUSTOM',
              customStartDate: prevPeriod.startDate,
              customEndDate: prevPeriod.endDate,
              campaignFilter: selectedCampaignFilter || null,
              campaignTypeFilter: selectedCampaignTypeFilter || null
            };

            if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
              prevRequestBody.campaignIds = labelFilteredCampaignIds;
            }

            const prevResponse = await axios.post(
              `${config.API_URL}/api/dashboard/components/audiences`,
              prevRequestBody,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (prevResponse.data.success) {
              const prevAudiences = prevResponse.data.data.audiences || [];
              console.log(`👥 [AUDIENCES] ${prevAudiences.length} audiences période précédente`);
              setPreviousAudiencesData(prevAudiences);
            }
          } catch (prevError) {
            console.warn(`⚠️ [AUDIENCES] Erreur chargement période précédente:`, prevError.message);
            setPreviousAudiencesData([]);
          } finally {
            setLoadingPreviousPeriod(false);
          }
        } else {
          setPreviousAudiencesData([]);
        }
      } else {
        console.log('👥 [AUDIENCES] Erreur dans la réponse:', response.data.error);
        setError(response.data.error || 'Erreur lors du chargement des audiences');
        setAudiencesData([]);
        setPreviousAudiencesData([]);
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error('👥 [AUDIENCES] Erreur chargement:', error);
      setError(`Erreur lors du chargement des audiences: ${error.message}`);
      setAudiencesData([]);
      setPreviousAudiencesData([]);
      setIsDataLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, labelFilteredCampaignIds, token, setError, compareEnabled, comparisonType]);

  // Détecter si Performance Max est sélectionné (doit être déclaré AVANT les useEffects qui l'utilisent)
  const isPmaxSelected =
    // Via le filtre par type de campagne
    selectedCampaignTypeFilter?.type === 10 ||
    selectedCampaignTypeFilter?.name === 'Performance Max' ||
    // Via le filtre par campagne individuelle
    (selectedCampaignFilter && (
      selectedCampaignFilter.name?.includes('PMAX') ||
      selectedCampaignFilter.name?.includes('PERFMAX') ||
      selectedCampaignFilter.name?.includes('Performance Max') ||
      selectedCampaignFilter.type === 10 ||
      selectedCampaignFilter.advertisingChannelType === 10
    ));

  // Ref pour tracker le premier montage et la période précédente
  const isInitialMount = React.useRef(true);
  const prevPeriodRef = React.useRef(selectedPeriod);

  // Recharger les données quand les paramètres changent (période, compte, filtres)
  useEffect(() => {
    // Ne pas charger si le module est masqué
    if (!isVisible) return;

    // Premier montage : ne pas recharger, laisser le useEffect de visibilité gérer
    if (isInitialMount.current) {
      isInitialMount.current = false;
      console.log('👥 [AUDIENCES-PARAMS] Premier montage, skip le reload automatique');
      return;
    }

    // Vérifier si la période a changé
    const periodChanged = prevPeriodRef.current !== selectedPeriod;
    prevPeriodRef.current = selectedPeriod;

    console.log('👥 [AUDIENCES-PARAMS] 🔴 Paramètres changés. Period changed:', periodChanged, 'selectedPeriod:', selectedPeriod);

    // Forcer le rechargement
    setAudiencesData([]);
    setPreviousAudiencesData([]);
    setIsDataLoaded(false);

    // Appeler le chargement directement si pas PMAX
    if (!isPmaxSelected) {
      console.log('👥 [AUDIENCES-PARAMS] ✅ Rechargement direct des données');
      loadAudiencesData();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, labelFilteredCampaignIds, isVisible, isPmaxSelected, loadAudiencesData, compareEnabled, comparisonType]);

  // Charger les données au premier affichage (quand le composant devient visible)
  useEffect(() => {
    console.log('👥 [AUDIENCES-VISIBILITY] Check:', { isVisible, isDataLoaded, isPmaxSelected });

    // Les données préchargées ne couvrent que la période courante. Quand une comparaison
    // est demandée, ce raccourci écrasait previousAudiencesData avec un tableau vide et la
    // colonne de variation restait donc vide : on passe alors par le chargement complet,
    // qui récupère les deux périodes.
    const wantsComparison = compareEnabled && comparisonType;
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0 && !wantsComparison) {
      const audiences = preloadedData.data?.audiences || [];
      console.log(`👥 [AUDIENCES-FRONTEND] Utilisation données préchargées: ${audiences.length} audiences (pas de comparaison demandée)`);
      setAudiencesData(audiences);
      setIsDataLoaded(true);
      setPreviousAudiencesData([]); // Pas de données de comparaison en mode préchargé
      return;
    }

    if (isVisible && !isDataLoaded && !isPmaxSelected) {
      console.log('👥 [AUDIENCES-VISIBILITY] ✅ Chargement initial');
      loadAudiencesData();
    }
  }, [isVisible, loadAudiencesData, isDataLoaded, isPmaxSelected, preloadedData, selectedCampaignFilter, selectedCampaignTypeFilter, selectedLabelsFilter, compareEnabled, comparisonType]);

  // Fonction de tri
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Données triées et paginées
  const processedData = useMemo(() => {
    if (!audiencesData || !Array.isArray(audiencesData)) {
      return [];
    }

    return audiencesData.map(audience => ({
      ...audience,
      ctr: audience.clicks > 0 && audience.impressions > 0
        ? ((audience.clicks / audience.impressions) * 100).toFixed(2)
        : '0.00',
      cpc: audience.clicks > 0
        ? (audience.cost / audience.clicks).toFixed(2)
        : '0.00',
      cpa: audience.conversions > 0
        ? (audience.cost / audience.conversions).toFixed(2)
        : '0.00',
      conversionRate: audience.clicks > 0 && audience.conversions > 0
        ? ((audience.conversions / audience.clicks) * 100).toFixed(2)
        : '0.00',
      cos: audience.conversionsValue > 0
        ? ((audience.cost / audience.conversionsValue) * 100).toFixed(2)
        : '0.00',
      roas: audience.cost > 0
        ? ((audience.conversionsValue / audience.cost) * 100).toFixed(2)
        : '0.00',
      roi: (audience.cost > 0 && audience.conversionsValue > 0)
        ? ((audience.conversionsValue - audience.cost) / audience.cost).toFixed(2)
        : '0.00'
    }));
  }, [audiencesData]);

  // Données traitées de la période précédente
  const processedPreviousData = useMemo(() => {
    if (!previousAudiencesData || !Array.isArray(previousAudiencesData)) {
      return [];
    }

    return previousAudiencesData.map(audience => ({
      ...audience,
      ctr: audience.clicks > 0 && audience.impressions > 0
        ? ((audience.clicks / audience.impressions) * 100)
        : 0,
      cpc: audience.clicks > 0
        ? (audience.cost / audience.clicks)
        : 0,
      cpa: audience.conversions > 0
        ? (audience.cost / audience.conversions)
        : 0,
      conversionRate: audience.clicks > 0 && audience.conversions > 0
        ? ((audience.conversions / audience.clicks) * 100)
        : 0,
      cos: audience.conversionsValue > 0
        ? ((audience.cost / audience.conversionsValue) * 100)
        : 0,
      roas: audience.cost > 0
        ? ((audience.conversionsValue / audience.cost) * 100)
        : 0,
      roi: (audience.cost > 0 && audience.conversionsValue > 0)
        ? ((audience.conversionsValue - audience.cost) / audience.cost)
        : 0
    }));
  }, [previousAudiencesData]);

  const sortedData = useMemo(() => {
    if (!processedData.length) return [];

    const sorted = [...processedData].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const numA = parseFloat(aVal) || 0;
      const numB = parseFloat(bVal) || 0;

      return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
    });

    return sorted;
  }, [processedData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Calculs des totaux
  const totals = useMemo(() => {
    return processedData.reduce((acc, audience) => ({
      impressions: acc.impressions + (audience.impressions || 0),
      clicks: acc.clicks + (audience.clicks || 0),
      cost: acc.cost + (audience.cost || 0),
      conversions: acc.conversions + (audience.conversions || 0),
      conversionsValue: acc.conversionsValue + (audience.conversionsValue || 0),
      ...cumulerVideo(acc, audience)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });
  }, [processedData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !processedPreviousData.length) return null;

    const baseTotals = processedPreviousData.reduce((acc, audience) => ({
      impressions: acc.impressions + (audience.impressions || 0),
      clicks: acc.clicks + (audience.clicks || 0),
      cost: acc.cost + (audience.cost || 0),
      conversions: acc.conversions + (audience.conversions || 0),
      conversionsValue: acc.conversionsValue + (audience.conversionsValue || 0),
      ...cumulerVideo(acc, audience)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      roas: baseTotals.cost > 0 ? (baseTotals.conversionsValue / baseTotals.cost * 100) : 0,
      roi: (baseTotals.cost > 0 && baseTotals.conversionsValue > 0) ? ((baseTotals.conversionsValue - baseTotals.cost) / baseTotals.cost) : 0
    };
  }, [compareEnabled, processedPreviousData]);

  const overallMetrics = useMemo(() => ({
    ctr: totals.clicks > 0 && totals.impressions > 0
      ? ((totals.clicks / totals.impressions) * 100).toFixed(2)
      : '0.00',
    cpc: totals.clicks > 0
      ? (totals.cost / totals.clicks).toFixed(2)
      : '0.00',
    cpa: totals.conversions > 0
      ? (totals.cost / totals.conversions).toFixed(2)
      : '0.00',
    conversionRate: totals.clicks > 0 && totals.conversions > 0
      ? ((totals.conversions / totals.clicks) * 100).toFixed(2)
      : '0.00',
    cos: totals.conversionsValue > 0
      ? ((totals.cost / totals.conversionsValue) * 100).toFixed(2)
      : '0.00',
    roas: totals.cost > 0
      ? ((totals.conversionsValue / totals.cost) * 100).toFixed(2)
      : '0.00',
    roi: (totals.cost > 0 && totals.conversionsValue > 0)
      ? ((totals.conversionsValue - totals.cost) / totals.cost).toFixed(2)
      : '0.00'
  }), [totals]);

  // Calcul du "trafic sans audiences" = global - avec audiences
  const trafficSummary = useMemo(() => {
    if (!globalTotals) return null;

    const withAudiences = {
      impressions: totals.impressions,
      clicks: totals.clicks,
      cost: totals.cost,
      conversions: totals.conversions,
      conversionsValue: totals.conversionsValue,
      views: totals.views || 0,
      videoImpressions: totals.videoImpressions || 0,
      viewCost: totals.viewCost || 0
    };

    const withoutAudiences = {
      impressions: Math.max(0, (globalTotals.impressions || 0) - totals.impressions),
      clicks: Math.max(0, (globalTotals.clicks || 0) - totals.clicks),
      cost: Math.max(0, (globalTotals.cost || 0) - totals.cost),
      conversions: Math.max(0, (globalTotals.conversions || 0) - totals.conversions),
      conversionsValue: Math.max(0, (globalTotals.conversionsValue || 0) - totals.conversionsValue),
      // Le complément « sans audience » n'a de sens que si le total global porte la vidéo ;
      // sinon il vaut zéro, et la colonne affiche un tiret plutôt qu'un chiffre inventé.
      views: Math.max(0, (globalTotals.views || 0) - (totals.views || 0)),
      videoImpressions: Math.max(0, (globalTotals.videoImpressions || 0) - (totals.videoImpressions || 0)),
      viewCost: Math.max(0, (globalTotals.viewCost || 0) - (totals.viewCost || 0))
    };

    // Calculer les métriques dérivées
    const withAudiencesMetrics = {
      ...withAudiences,
      ctr: withAudiences.impressions > 0 ? ((withAudiences.clicks / withAudiences.impressions) * 100).toFixed(2) : '0.00',
      cpc: withAudiences.clicks > 0 ? (withAudiences.cost / withAudiences.clicks).toFixed(2) : '0.00',
      cpa: withAudiences.conversions > 0 ? (withAudiences.cost / withAudiences.conversions).toFixed(2) : '0.00',
      conversionRate: withAudiences.clicks > 0 ? ((withAudiences.conversions / withAudiences.clicks) * 100).toFixed(2) : '0.00',
      cos: withAudiences.conversionsValue > 0 ? ((withAudiences.cost / withAudiences.conversionsValue) * 100).toFixed(2) : '0.00',
      roas: withAudiences.cost > 0 ? ((withAudiences.conversionsValue / withAudiences.cost) * 100).toFixed(2) : '0.00',
      roi: (withAudiences.cost > 0 && withAudiences.conversionsValue > 0) ? ((withAudiences.conversionsValue - withAudiences.cost) / withAudiences.cost).toFixed(2) : '0.00'
    };

    const withoutAudiencesMetrics = {
      ...withoutAudiences,
      ctr: withoutAudiences.impressions > 0 ? ((withoutAudiences.clicks / withoutAudiences.impressions) * 100).toFixed(2) : '0.00',
      cpc: withoutAudiences.clicks > 0 ? (withoutAudiences.cost / withoutAudiences.clicks).toFixed(2) : '0.00',
      cpa: withoutAudiences.conversions > 0 ? (withoutAudiences.cost / withoutAudiences.conversions).toFixed(2) : '0.00',
      conversionRate: withoutAudiences.clicks > 0 ? ((withoutAudiences.conversions / withoutAudiences.clicks) * 100).toFixed(2) : '0.00',
      cos: withoutAudiences.conversionsValue > 0 ? ((withoutAudiences.cost / withoutAudiences.conversionsValue) * 100).toFixed(2) : '0.00',
      roas: withoutAudiences.cost > 0 ? ((withoutAudiences.conversionsValue / withoutAudiences.cost) * 100).toFixed(2) : '0.00',
      roi: (withoutAudiences.cost > 0 && withoutAudiences.conversionsValue > 0) ? ((withoutAudiences.conversionsValue - withoutAudiences.cost) / withoutAudiences.cost).toFixed(2) : '0.00'
    };

    // Calculer les données de comparaison pour "sans audiences" si disponible
    let previousWithoutAudiences = null;
    if (compareEnabled && previousGlobalTotals && previousTotals) {
      const prevWithout = {
        impressions: Math.max(0, (previousGlobalTotals.impressions || 0) - (previousTotals.impressions || 0)),
        clicks: Math.max(0, (previousGlobalTotals.clicks || 0) - (previousTotals.clicks || 0)),
        cost: Math.max(0, (previousGlobalTotals.cost || 0) - (previousTotals.cost || 0)),
        conversions: Math.max(0, (previousGlobalTotals.conversions || 0) - (previousTotals.conversions || 0)),
        conversionsValue: Math.max(0, (previousGlobalTotals.conversionsValue || 0) - (previousTotals.conversionsValue || 0))
      };
      previousWithoutAudiences = {
        ...prevWithout,
        ctr: prevWithout.impressions > 0 ? (prevWithout.clicks / prevWithout.impressions * 100) : 0,
        cpc: prevWithout.clicks > 0 ? (prevWithout.cost / prevWithout.clicks) : 0,
        cpa: prevWithout.conversions > 0 ? (prevWithout.cost / prevWithout.conversions) : 0,
        conversionRate: prevWithout.clicks > 0 ? (prevWithout.conversions / prevWithout.clicks * 100) : 0,
        cos: prevWithout.conversionsValue > 0 ? (prevWithout.cost / prevWithout.conversionsValue * 100) : 0,
        roas: prevWithout.cost > 0 ? (prevWithout.conversionsValue / prevWithout.cost * 100) : 0,
        roi: (prevWithout.cost > 0 && prevWithout.conversionsValue > 0) ? ((prevWithout.conversionsValue - prevWithout.cost) / prevWithout.cost) : 0
      };
    }

    // Calculer les métriques totales (globalTotals avec métriques dérivées)
    const totalMetrics = {
      impressions: globalTotals.impressions || 0,
      clicks: globalTotals.clicks || 0,
      cost: globalTotals.cost || 0,
      conversions: globalTotals.conversions || 0,
      conversionsValue: globalTotals.conversionsValue || 0,
      ctr: globalTotals.impressions > 0 ? ((globalTotals.clicks / globalTotals.impressions) * 100).toFixed(2) : '0.00',
      cpc: globalTotals.clicks > 0 ? (globalTotals.cost / globalTotals.clicks).toFixed(2) : '0.00',
      cpa: globalTotals.conversions > 0 ? (globalTotals.cost / globalTotals.conversions).toFixed(2) : '0.00',
      conversionRate: globalTotals.clicks > 0 ? ((globalTotals.conversions / globalTotals.clicks) * 100).toFixed(2) : '0.00',
      cos: globalTotals.conversionsValue > 0 ? ((globalTotals.cost / globalTotals.conversionsValue) * 100).toFixed(2) : '0.00',
      roas: globalTotals.cost > 0 ? ((globalTotals.conversionsValue / globalTotals.cost) * 100).toFixed(2) : '0.00',
      roi: (globalTotals.cost > 0 && globalTotals.conversionsValue > 0) ? ((globalTotals.conversionsValue - globalTotals.cost) / globalTotals.cost).toFixed(2) : '0.00',
      // Vidéo : grandeurs sommables uniquement. Le taux et le CPV de la ligne Total sont
      // dérivés par la fabrique de colonnes, comme pour toutes les autres lignes.
      views: globalTotals.views || 0,
      videoImpressions: globalTotals.videoImpressions || 0,
      viewCost: globalTotals.viewCost || 0
    };

    return { withAudiences: withAudiencesMetrics, withoutAudiences: withoutAudiencesMetrics, previousWithoutAudiences, total: totalMetrics };
  }, [totals, globalTotals, compareEnabled, previousGlobalTotals, previousTotals]);

  // Données pour les graphiques en secteurs (clics et conversions) - toutes les audiences
  const topAudiences = useMemo(() => {
    if (!sortedData.length) return [];
    return sortedData;
  }, [sortedData]);

  const clicksChartData = useMemo(() => {
    if (!topAudiences.length) return [];
    // Limiter à 15 éléments pour le graphique
    const top15 = topAudiences.slice(0, 15);
    const totalClicks = top15.reduce((sum, a) => sum + (a.clicks || 0), 0);

    return top15.map(audience => ({
      name: (audience.audienceName || audience.audienceSegment || 'Audience').substring(0, 25) + ((audience.audienceName || '').length > 25 ? '...' : ''),
      value: audience.clicks || 0,
      percentage: totalClicks > 0 ? ((audience.clicks / totalClicks) * 100).toFixed(1) : 0
    }));
  }, [topAudiences]);

  const conversionsChartData = useMemo(() => {
    if (!topAudiences.length) return [];
    // Limiter à 15 éléments pour le graphique
    const top15 = topAudiences.slice(0, 15);
    const totalConversions = top15.reduce((sum, a) => sum + (a.conversions || 0), 0);

    return top15.map(audience => ({
      name: (audience.audienceName || audience.audienceSegment || 'Audience').substring(0, 25) + ((audience.audienceName || '').length > 25 ? '...' : ''),
      value: audience.conversions || 0,
      percentage: totalConversions > 0 ? ((audience.conversions / totalConversions) * 100).toFixed(1) : 0
    }));
  }, [topAudiences]);

  const legendData = useMemo(() => {
    if (!topAudiences.length) return [];

    // Limiter la légende à 15 éléments maximum
    return topAudiences.slice(0, 15).map((audience, index) => ({
      value: (audience.audienceName || audience.audienceSegment || 'Audience').substring(0, 20) + ((audience.audienceName || '').length > 20 ? '...' : ''),
      type: 'rect',
      color: COLORS[index % COLORS.length]
    }));
  }, [topAudiences, COLORS]);

  // Fonction pour obtenir l'icône de tri (même style que GenderTableModular)
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // Fonction pour trouver la ligne correspondante dans les données précédentes
  const findPreviousAudience = useCallback((audience) => {
    if (!compareEnabled || !processedPreviousData.length) return null;

    // Chercher par audienceId d'abord, puis par audienceName
    return processedPreviousData.find(prev =>
      (audience.audienceId && prev.audienceId === audience.audienceId) ||
      (audience.criterionId && prev.criterionId === audience.criterionId) ||
      (audience.audienceName && prev.audienceName === audience.audienceName)
    );
  }, [compareEnabled, processedPreviousData]);

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  // Masquer le composant si aucune donnée n'est disponible (après chargement)
  if (!loading && isDataLoaded && audiencesData.length === 0) {
    return null;
  }

  // Si Performance Max est sélectionné, afficher un message informatif
  if (isPmaxSelected) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4>
            <Users size={16} />&nbsp;
            Audiences <span style={{ fontSize: '0.7em', fontWeight: 'normal', color: '#888' }}>(hors Performance Max)</span>
          </h4>
        </div>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666',
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <div style={{ fontSize: '2em', marginBottom: '15px' }}>🚫</div>
          <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>Données d'audiences non disponibles</h4>
          <p style={{ margin: '0 0 15px 0', fontSize: '0.95em', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
            Les campagnes <strong>Performance Max</strong> ne fournissent pas de données d'audiences détaillées via cette vue.
          </p>
          <p style={{ margin: '0', fontSize: '0.85em', fontStyle: 'italic', color: '#888' }}>
            Les audiences sont gérées via les signaux d'audience dans les groupes de composants.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-table-section">
      {/* Tooltip des types d'audiences - rendu en dehors du flux du tableau */}
      {showTypeTooltip && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '16px',
          backgroundColor: '#1f2937',
          color: '#fff',
          borderRadius: '12px',
          fontSize: '13px',
          width: '320px',
          zIndex: 99999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          lineHeight: '1.6',
          textAlign: 'left',
          pointerEvents: 'none'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#60a5fa' }}>Affinité</strong><br />
            <span style={{ color: '#d1d5db' }}>Audiences basées sur les centres d'intérêt et passions des utilisateurs.</span>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#34d399' }}>Sur le marché</strong><br />
            <span style={{ color: '#d1d5db' }}>Audiences recherchant activement des produits ou services similaires.</span>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#f59e0b' }}>Visiteurs du site Web</strong><br />
            <span style={{ color: '#d1d5db' }}>Audiences de remarketing basées sur les visiteurs de votre site.</span>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#ec4899' }}>Données démographiques détaillées</strong><br />
            <span style={{ color: '#d1d5db' }}>Audiences ciblées par caractéristiques démographiques.</span>
          </div>
          <div>
            <strong style={{ color: '#8b5cf6' }}>Liste des clients</strong><br />
            <span style={{ color: '#d1d5db' }}>Audiences basées sur vos données CRM (emails, téléphones).</span>
          </div>
        </div>
      )}

      <div className="section-header">
        <h4>
          <Users size={16} />&nbsp;
          Audiences <span style={{ fontSize: '0.7em', fontWeight: 'normal', color: '#888' }}>(hors Performance Max)</span>
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Audiences"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {/* Tableau récapitulatif Avec/Sans audiences */}
      {!loading && trafficSummary && processedData.length > 0 && (
        <div className="campaigns-table-wrapper" style={{ marginBottom: '20px' }}>
          <table className="campaigns-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>Segment</th>
                {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => (
                  <th key={metricKey} className="numeric">{metricConfig[metricKey].label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: '#22c55e',
                      width: '10px',
                      minWidth: '10px',
                      height: '10px',
                      minHeight: '10px',
                      borderRadius: '50%',
                      marginRight: '8px',
                      flexShrink: 0
                    }}></span>
                    Avec audiences
                  </div>
                </td>
                {/* Rendu dynamique des cellules selon l'ordre configuré */}
                {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                  const config = metricConfig[metricKey];
                  const className = config.getClassName ? `numeric ${config.getClassName(trafficSummary.withAudiences)}` : 'numeric';
                  return (
                    <td key={metricKey} className={className}>
                      {config.renderSummaryCell(trafficSummary.withAudiences, previousTotals || {}, !!previousTotals)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: '#ef4444',
                      width: '10px',
                      minWidth: '10px',
                      height: '10px',
                      minHeight: '10px',
                      borderRadius: '50%',
                      marginRight: '8px',
                      flexShrink: 0
                    }}></span>
                    Sans audiences
                  </div>
                </td>
                {/* Rendu dynamique des cellules selon l'ordre configuré */}
                {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                  const config = metricConfig[metricKey];
                  const className = config.getClassName ? `numeric ${config.getClassName(trafficSummary.withoutAudiences)}` : 'numeric';
                  return (
                    <td key={metricKey} className={className}>
                      {config.renderSummaryCell(trafficSummary.withoutAudiences, trafficSummary.previousWithoutAudiences || {}, !!trafficSummary.previousWithoutAudiences)}
                    </td>
                  );
                })}
              </tr>
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #e5e7eb' }}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Total
                  </div>
                </td>
                {/* Rendu dynamique des cellules Total selon l'ordre configuré */}
                {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                  const config = metricConfig[metricKey];
                  const className = config.getClassName ? `numeric ${config.getClassName(trafficSummary.total)}` : 'numeric';
                  return (
                    <td key={metricKey} className={className}>
                      {config.renderSummaryCell(trafficSummary.total, previousGlobalTotals || {}, !!previousGlobalTotals)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <p>Chargement des audiences...</p>
        </div>
      ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
        <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
          Chargement des données de comparaison...
        </div>
      ) : !processedData.length ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>Aucune audience trouvée</h3>
          <p>Aucune donnée d'audience disponible pour cette période</p>
        </div>
      ) : (
        <>
          {/* Graphiques en secteurs côte à côte */}
          {clicksChartData.length > 0 && conversionsChartData.length > 0 && (
            <div style={{ marginBottom: '20px', background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* Pie chart Clics (gauche) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par audience</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={clicksChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => percentage > 5 ? `${percentage}%` : ''}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {clicksChartData.map((entry, index) => (
                          <Cell key={`clicks-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [formatNumber(value) + ' clics', name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Légende commune (centre) */}
                <div style={{
                  minWidth: '180px',
                  maxWidth: '220px',
                  padding: '0 15px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {legendData.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '6px',
                      fontSize: '11px'
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: item.color,
                        marginRight: '6px',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}></div>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</span>
                    </div>
                  ))}
                </div>

                {/* Pie chart Conversions (droite) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par audience</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={conversionsChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => percentage > 5 ? `${percentage}%` : ''}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {conversionsChartData.map((entry, index) => (
                          <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [formatNumber(value) + ' conversions', name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <div className="campaigns-table-wrapper">
            <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('audienceName')} className="sortable">
                    Audience {getSortIcon('audienceName')}
                  </th>
                  <th onClick={() => handleSort('audienceType')} className="sortable">
                    Type
                    <span
                      style={{ marginLeft: '4px', cursor: 'help', display: 'inline-flex', verticalAlign: 'middle' }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={() => setShowTypeTooltip(true)}
                      onMouseLeave={() => setShowTypeTooltip(false)}
                    >
                      <HelpCircle size={14} style={{ color: '#6b7280' }} />
                    </span>
                    {getSortIcon('audienceType')}
                  </th>
                  {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                        {config.label} {getSortIcon(config.sortKey)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((audience, index) => {
                  // Trouver la ligne correspondante dans les données de la période précédente
                  const prevAudience = findPreviousAudience(audience);
                  const hasComparison = !!prevAudience;

                  return (
                    <tr key={`${audience.criterionId}-${index}`}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px', fontSize: '1.2em' }}>👥</span>
                          {audience.audienceName || audience.audienceSegment || 'Audience inconnue'}
                        </div>
                      </td>
                      <td>{audience.audienceType || 'N/A'}</td>
                      {/* Rendu dynamique des cellules selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        const className = config.getClassName ? `numeric ${config.getClassName(audience)}` : 'numeric';
                        return (
                          <td key={metricKey} className={className}>
                            {config.renderCell(audience, prevAudience || {}, hasComparison)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td className="device-name"><strong>TOTAL</strong></td>
                  <td><strong>-</strong></td>
                  {/* Rendu dynamique des totaux selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(overallMetrics)}` : 'numeric';
                    return (
                      <td key={metricKey} className={className}>
                        {config.renderTotal(totals, previousTotals, overallMetrics)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedData.length)} sur {sortedData.length} audiences
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="audiences"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Audiences"
      />
    </div>
  );
});

AudiencesTableModular.displayName = 'AudiencesTableModular';

export default AudiencesTableModular;
